import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { regularExpressions } from '~/constants/regularExpressions';
import {
  addressSchema,
  documentSchema,
  foreignTaxNumberSchema,
  identificationNumberSchema,
  nameSchema,
  notRequiredOptionSchema,
  requiredOptionSchema,
  RNOKPPIdentNumberSchema,
} from '../../validationSchemas';
import { OwnerType, TOwner, TOwnerLink, TOwnerRange } from './types';
import { CustomResolveOptions } from '../../types';
import { getIsFirstLevel } from './utils/utils.tsx';
import { latinAndUkrCharactersOnly } from '~/helpers/validation.ts';
import { UKRAINE_CODE } from '~/constants/common.ts';
import { roundNumber } from '~/helpers/utils.ts';
import { individualTypes, entitiesWithSimilarFields } from './constants.ts';

const isRangeStarted = (value: TOwnerRange | null | undefined) => {
  if (!value) return false;

  return Boolean(
    value?.number?.toString()?.trim() ||
    value?.from?.toString()?.trim() ||
    value?.to?.toString()?.trim()
  );
};

const testUnique = (
  value: string | null | undefined,
  context: Yup.TestContext,
  fieldName: string
) => {
  if (!value) return true;
  const isUnique =
    context.from?.[1]?.value?.owners?.filter(
      (item: TOwner) => item[fieldName as keyof typeof item] === value
    )?.length === 1;
  if (!isUnique) {
    return context.createError({
      path: `${context.path}`,
      message: errorMessages.OWNER_UNIQUE_IDENTIFICATION_NUMBER,
    });
  }
  return true;
};

const sharePercentSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .matches(regularExpressions.ROUNDING_NUMBER, errorMessages.INTEGER_OR_FLOAT(6))
  .test('positive', function (value) {
    if (!value || Number(value) <= 0) {
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_BE_GREATER_THAN_ZERO,
      });
    }
    return true;
  })
  .test('max-5', function (value) {
    if (!value || Number(value) >= 5) {
      return this.createError({
        path: this.path,
        message: errorMessages.MORE_THEN_PERCENT_SHARES(5),
      });
    }
    return true;
  });

const ownerIdentificationNumberSchema = Yup.string().when(
  ['type', 'foreignTaxNumber'],
  ([type, foreignTaxNumber], schema) => {
    if (!entitiesWithSimilarFields.includes(type)) {
      return schema.nullable().notRequired();
    }
    if (individualTypes.includes(type)) {
      return RNOKPPIdentNumberSchema('foreignTaxNumber').test(
        'unique-identification-number',
        function (value) {
          return testUnique(value, this, 'identificationNumber');
        }
      );
    }
    return identificationNumberSchema('foreignTaxNumber')
      .test('length-check', function (value) {
        const isEmpty = value === undefined || value === null || value === '';
        if (isEmpty && foreignTaxNumber) return true;

        if (isEmpty) {
          return this.createError({ message: errorMessages.FIELD_REQUIRED });
        }
        if (value.length < 8)
          return this.createError({ message: errorMessages.MINIMUM_NUMBER_OF_CHARACTERS(8) });
        if (value.length > 9)
          return this.createError({ message: errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(9) });

        return true;
      })
      .test('unique-identification-number', function (value) {
        return testUnique(value, this, 'identificationNumber');
      });
  }
);

const ownerRangeSchema = Yup.object({
  number: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .matches(regularExpressions.IDENTIFICATION_NUMBER, errorMessages.MUST_BE_NUMBER)
    .test('positive-integer', function (value) {
      if (!value || Number(value) <= 0) {
        return this.createError({
          path: this.path,
          message: errorMessages.INTEGER_GRATER_THAN_ZERO,
        });
      }
      return true;
    }),

  from: sharePercentSchema,
  to: sharePercentSchema,
});

const conditionalOwnerRangeSchema = Yup.lazy((value: TOwnerRange | null | undefined) => {
  if (!isRangeStarted(value)) {
    return Yup.object().nullable();
  }

  return ownerRangeSchema;
});

const russianBelarusianIndividualsSchema = Yup.object({
  fullName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  residencyStatus: Yup.object().when('$citizenshipStatus', ([], _, ctx) => {
    return ctx.parent?.citizenshipStatus?.value ? notRequiredOptionSchema : requiredOptionSchema;
  }),
  citizenshipStatus: Yup.object().when('residencyStatus', ([], _, ctx) => {
    return ctx.parent?.residencyStatus?.value ? notRequiredOptionSchema : requiredOptionSchema;
  }),
  ownershipShare: sharePercentSchema,
});

const russianBelarusianLegalEntitiesSchema = Yup.object({
  entityName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  residencyStatus: requiredOptionSchema,
  ownershipShare: sharePercentSchema,
});

const ownershipPersentageSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
    if (!value) return false;
    return !/^0\d/.test(value);
  })
  .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
  .test('is-number', function (value) {
    if (value === null || value === undefined) return true;

    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_BE_NUMBER,
      });
    }

    return true;
  })
  .test('is-non-negative', errorMessages.MUST_BE_GREATER_THAN_ZERO, (value, context) => {
    const owners = context.from?.[1]?.value?.owners;
    const currentOwner = owners.find((owner: TOwner) => owner.id === context.parent.fromNodeId);
    if (currentOwner.type === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP) return true;

    if (!value) return false;
    if (value.trim().startsWith('-')) return false;
    const num = Number(value);
    if (Number.isNaN(num)) return true;
    return num > 0;
  })
  .test('max-decimal-places', function (value) {
    if (value === null || value === undefined) return true;

    if (!regularExpressions.ROUNDING_NUMBER.test(String(value)))
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_HAVE_ONE_DECIMAL_NUMBER(6),
      });

    return true;
  })
  .test('more-than-zero', function (value, context) {
    if (value === null || value === undefined) return true;
    const owners = context.from?.[1]?.value?.owners;
    const currentOwner = owners.find((owner: TOwner) => owner.id === context.parent.fromNodeId);

    if (
      currentOwner?.areIndividualsWithDecisiveInfluenceAbsent ||
      currentOwner.type === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP
    )
      return true;

    if (+value < 0)
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_BE_POSITIVE_OR_ZERO,
      });

    return true;
  })
  .test('less-equal-100', function (value, context) {
    if (value === null || value === undefined) return true;
    const links = this.from?.[1]?.value.link;
    const owners = this.from?.[1]?.value.owners;
    const currentLink = context.parent;
    const parentLinks = links.filter(
      (link: TOwnerLink) => link.fromNodeId === currentLink.toNodeId
    );
    const parentOwners = owners.filter((owner: TOwner) =>
      parentLinks.some((link: TOwnerLink) => link.fromNodeId === owner.id)
    );
    const isIndividualOwner = parentOwners.some(
      (owner: TOwner) => owner.type === OwnerType.INDIVIDUAL
    );

    if (isIndividualOwner && +value !== 100) {
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_BE_EQUAL_100,
      });
    }
    if (+value > 100)
      return this.createError({
        path: this.path,
        message: errorMessages.MUST_BE_LESS_OR_EQUAL_100,
      });

    return true;
  });

const isBeneficiarySchema = Yup.boolean()
  .nullable()
  .when('type', {
    is: (type: OwnerType) => individualTypes.includes(type),
    then: (schema) => schema.required(errorMessages.YES_NO_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  });

const ownerSchema = Yup.object()
  .shape({
    id: Yup.string().nullable(),
    lastName: Yup.string().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: () => nameSchema.required(errorMessages.FIELD_REQUIRED),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    firstName: Yup.string().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: () => nameSchema.required(errorMessages.FIELD_REQUIRED),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    middleName: nameSchema.notRequired(),
    type: Yup.string(),
    fullName: Yup.string().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) =>
        schema
          .nullable()
          .concat(latinAndUkrCharactersOnly)
          .max(150, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(150))
          .required(errorMessages.FIELD_REQUIRED),
    }),
    shortNameUkr: Yup.string().when('type', ([type], schema) => {
      return type === OwnerType.LEGAL_ENTITY
        ? schema
            .concat(latinAndUkrCharactersOnly)
            .max(38, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(38))
            .required(errorMessages.FIELD_REQUIRED)
        : schema.notRequired().nullable();
    }),
    identificationNumber: ownerIdentificationNumberSchema,
    foreignTaxNumber: Yup.string().when('type', function ([type], schema) {
      if (entitiesWithSimilarFields.includes(type)) {
        return foreignTaxNumberSchema('identificationNumber').test(
          'unique-foreignTaxNumber-number',
          function (value) {
            return testUnique(value, this, 'foreignTaxNumber');
          }
        );
      } else {
        return schema.nullable().notRequired();
      }
    }),
    citizenshipCountry: Yup.object().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: () => requiredOptionSchema,
      otherwise: () => notRequiredOptionSchema,
    }),
    residenceCountry: Yup.object().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: () => requiredOptionSchema,
      otherwise: () => notRequiredOptionSchema,
    }),
    birthplaceCountry: Yup.object().when(
      ['type', 'id', 'isBeneficiary'],
      function ([type, id, isBeneficiary], _, options: CustomResolveOptions) {
        if (individualTypes.includes(type) && (getIsFirstLevel(options, id) || isBeneficiary)) {
          return requiredOptionSchema;
        } else return notRequiredOptionSchema;
      }
    ),
    birthPlace: Yup.string().when(
      ['type', 'id', 'isBeneficiary'],
      function ([type, id, isBeneficiary], schema, options: CustomResolveOptions) {
        if (individualTypes.includes(type) && (getIsFirstLevel(options, id) || isBeneficiary)) {
          return schema
            .max(120, errorMessages.MAX_LENGTH(120))
            .required(errorMessages.FIELD_REQUIRED);
        } else return schema.notRequired().nullable();
      }
    ),
    documents: Yup.object().when(
      ['type', 'id', 'isBeneficiary'],
      function ([type, id, isBeneficiary], schema, options: CustomResolveOptions) {
        if (individualTypes.includes(type) && (getIsFirstLevel(options, id) || isBeneficiary)) {
          return documentSchema;
        } else return schema.nullable().notRequired();
      }
    ),
    birthDate: Yup.string().when('type', {
      is: (type: OwnerType) => individualTypes.includes(type),
      then: (schema) => schema.required(errorMessages.FIELD_REQUIRED),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    address: Yup.object().when(
      ['type', 'id', 'isBeneficiary'],
      function ([type, id, isBeneficiary], schema, options: CustomResolveOptions) {
        if (entitiesWithSimilarFields.includes(type)) {
          return getIsFirstLevel(options, id) || isBeneficiary
            ? schema.shape({
                registrationAddress: addressSchema,
                permanentAddress: addressSchema,
                coincidences: Yup.boolean().nullable(),
              })
            : schema.shape({
                registrationAddress: Yup.object().shape({ country: requiredOptionSchema }),
                permanentAddress: Yup.object().shape({ country: requiredOptionSchema }),
              });
        } else {
          return schema.nullable().notRequired();
        }
      }
    ),
    isInTerList: Yup.boolean().nullable(),
    isBeneficiary: isBeneficiarySchema,
    typesOfActivity: Yup.object().when(
      ['type', 'id', 'residenceCountry'],
      function ([type, id, residenceCountry], _, options: CustomResolveOptions) {
        const isUkraine = residenceCountry?.value === UKRAINE_CODE;
        const isCountryNotSelected = !residenceCountry?.value;

        if (
          OwnerType.LEGAL_ENTITY === type &&
          getIsFirstLevel(options, id) &&
          (isUkraine || isCountryNotSelected)
        ) {
          return requiredOptionSchema;
        } else {
          return notRequiredOptionSchema;
        }
      }
    ),
    registrationDate: Yup.string().when(
      ['type', 'id'],
      function ([type, id], schema, options: CustomResolveOptions) {
        if (OwnerType.LEGAL_ENTITY === type && getIsFirstLevel(options, id)) {
          return schema.required(errorMessages.FIELD_REQUIRED);
        } else {
          return schema.nullable().notRequired();
        }
      }
    ),
    sourceURL: Yup.string().when('type', {
      is: OwnerType.FREE_FLOAT,
      then: (schema) =>
        schema
          .url(errorMessages.INVALID_URL_FORMAT)
          .required(errorMessages.EMPTY_STOCK_URL)
          .max(500, errorMessages.MAX_URL_LENGTH),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    isUSTaxResident: Yup.boolean().nullable(),
    isInBankBlackList: Yup.boolean().nullable(),
    isOtherTaxResident: Yup.boolean().nullable(),
    russianBelarusianIndividuals: Yup.array().when('type', {
      is: OwnerType.SHAREHOLDER_GROUP,
      then: () => Yup.array(russianBelarusianIndividualsSchema).nullable(),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    russianBelarusianLegalEntities: Yup.array().when('type', {
      is: OwnerType.SHAREHOLDER_GROUP,
      then: () => Yup.array(russianBelarusianLegalEntitiesSchema).nullable(),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    rangeLegal: Yup.object().when('type', {
      is: OwnerType.SHAREHOLDER_GROUP,
      then: () => conditionalOwnerRangeSchema,
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    rangeIndividual: Yup.object().when('type', {
      is: OwnerType.SHAREHOLDER_GROUP,
      then: () => conditionalOwnerRangeSchema,
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
    hasRussianOrBelarusianCitizenShareholders: Yup.boolean()
      .nullable()
      .when('type', {
        is: OwnerType.SHAREHOLDER_GROUP,
        then: (schema) => schema.required(errorMessages.REQUIRED_IS_FORBIDEN_COUNTRY),
        otherwise: (schema) => schema.notRequired(),
      }),
    areIndividualsWithDecisiveInfluenceAbsent: Yup.boolean().nullable(),
  })
  .test('at-least-one-group', function (value) {
    if (value?.type !== OwnerType.SHAREHOLDER_GROUP) return true;

    if (
      !isRangeStarted(value?.rangeIndividual as TOwnerRange) &&
      !isRangeStarted(value?.rangeLegal as TOwnerRange)
    ) {
      return this.createError({
        path: `${this.path}.shares`,
        message: errorMessages.AT_LEAST_ONE_RANGE,
      });
    } else {
      return true;
    }
  })
  .test('at-least-one-forbiden-item', function (value) {
    if (value.type !== OwnerType.SHAREHOLDER_GROUP) return true;
    if (
      value?.hasRussianOrBelarusianCitizenShareholders &&
      !value?.russianBelarusianIndividuals?.length &&
      !value?.russianBelarusianLegalEntities?.length
    ) {
      return this.createError({
        path: `${this.path}.forbidenOwners`,
        message: errorMessages.REQUIRED_FORBIDEN_COUNTRY_OWNER,
      });
    } else {
      return true;
    }
  })
  .test('no-owners', function (value, ctx) {
    const links = ctx.from?.[1]?.value?.link as TOwnerLink[];
    const isChildLinks = links.some((link) => link.toNodeId === value.id);

    if (value.areIndividualsWithDecisiveInfluenceAbsent) return true;

    if (value.type === OwnerType.LEGAL_ENTITY && !isChildLinks) {
      return this.createError({
        path: `${this.path}.noOwners`,
        message: errorMessages.EMPTY_LEGAL_OWNERS,
      });
    }

    return true;
  })
  .test('full-percentage', function (value, context) {
    if (value.type !== OwnerType.LEGAL_ENTITY || value?.areIndividualsWithDecisiveInfluenceAbsent)
      return true;

    const childLinks: TOwnerLink[] =
      context.from?.[1]?.value?.link?.filter((item: TOwnerLink) => item.toNodeId === value.id) ||
      [];
    const linkCount = childLinks.reduce(
      (map, link) => map.set(link.parentLinkId, (map.get(link.parentLinkId) || 0) + 1),
      new Map<string | null, number>()
    );
    const hasDuplicates = [...linkCount.values()].some((n) => n > 1);
    const filteredLinks = hasDuplicates
      ? childLinks.filter((link) => (linkCount.get(link.parentLinkId) ?? 0) > 1)
      : childLinks;

    const childrens: Record<string, TOwnerLink[]> = {};

    filteredLinks.forEach((item) => {
      childrens[item.parentLinkId as string] = [
        ...(childrens[item.parentLinkId as string] || []),
        item,
      ];
    });

    const correctPercentage = Object.keys(childrens).some(
      (item) =>
        roundNumber(
          childrens[item]?.reduce((prev, current) => +prev + +(current.ownershipPercentage || 0), 0)
        ) === 100
    );

    if (!correctPercentage) {
      return this.createError({
        path: `${context.path}.ownershipPercentageTotal`,
        message: errorMessages.AMOUNT_100_PERCENT,
      });
    } else {
      return true;
    }
  });

export const validationSchema = Yup.object().shape({
  owners: Yup.array().of(ownerSchema),
  ownershipPercentageTotal: Yup.number()
    .typeError(errorMessages.MUST_BE_NUMBER)
    .test('is-100', function (value, ctx) {
      if (ctx.parent.isFoundersAndBeneficiariesAbsent) return true;
      else if (value === null || value === undefined) return true;
      else if (+value === 100) return true;
      else
        return this.createError({
          path: this.path,
          message: errorMessages.AMOUNT_100_PERCENT,
        });
    })
    .required(errorMessages.FIELD_REQUIRED)
    .nullable(),
  link: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().notRequired().nullable(),
        fromNodeId: Yup.string().notRequired().nullable(),
        toNodeId: Yup.string().notRequired().nullable(),
        ownershipPercentage: ownershipPersentageSchema,
      })
    )
    .notRequired(),
  isFoundersAndBeneficiariesAbsent: Yup.boolean()
    .nullable()
    .test('no-owners', function (value, ctx) {
      if (!value && !ctx.parent.owners?.length) {
        return this.createError({
          path: this.path,
          message: errorMessages.EMPTY_OWNERS,
        });
      }

      return true;
    }),
  ownershipStructureSpecifics: Yup.string().notRequired().nullable(),
});
